import type {
  SelectableData,
  SelectModeBoundingRect,
} from "schemas/generated/selectMode";

import React from "react";

import { filterNulls } from "replo-utils/lib/array";
import { isIntersecting, isRectEnclosing } from "replo-utils/lib/geometry";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  readSelectablePayload,
  SELECT_MODE_SELECTABLE_ATTRIBUTE,
  SELECT_MODE_SELECTABLE_PAYLOAD_ATTRIBUTE,
} from "schemas/selectMode";
import { v4 as uuidv4 } from "uuid";

import { useBedrock } from "../BedrockProvider";
import {
  clearSelectionHighlights,
  setHoveredSelectableKey,
  setSelectedSelectableKeys,
} from "./selectionHighlightStore";
import { useSelectModeInteractions } from "./useSelectModeInteractions";

function getSelectablePayloadKey(element: Element): string {
  return element.getAttribute(SELECT_MODE_SELECTABLE_PAYLOAD_ATTRIBUTE) ?? "";
}

type PointerPosition = {
  clientX: number;
  clientY: number;
};

// A selectable plus a stable id, snapshotted when a drag begins. The id
// keys the parent's live highlight so it isn't remounted every move.
type SelectableCandidate = {
  id: string;
  data: SelectableData;
  // Raw payload attribute string, used as the highlight-store key so the
  // committed in-iframe border matches the right selectable.
  payloadKey: string;
};

type DragSession = {
  start: PointerPosition;
  current: PointerPosition;
  hasExceededThreshold: boolean;
  candidates: SelectableCandidate[];
};

const IDLE_DRAG_SESSION: DragSession | null = null;
const DRAG_THRESHOLD_PX = 6;

/**
 * Note (Noah, 2026-05-27): Tolerance for selection of the drag rect - boxes
 * within this number of pixels outside of the actual drag box are still counted
 * as selected. Effectively, the drag box is expanded by this number of pixels
 * on each side.
 */
const SELECTION_TOLERANCE_PX = 4;

function getRectFromDomRect(domRect: DOMRect): SelectModeBoundingRect {
  return {
    x: domRect.x,
    y: domRect.y,
    left: domRect.left,
    top: domRect.top,
    right: domRect.right,
    bottom: domRect.bottom,
    width: domRect.width,
    height: domRect.height,
  };
}

function getSelectionRect({
  start,
  current,
}: {
  start: PointerPosition;
  current: PointerPosition;
}): SelectModeBoundingRect {
  const left = Math.min(start.clientX, current.clientX);
  const right = Math.max(start.clientX, current.clientX);
  const top = Math.min(start.clientY, current.clientY);
  const bottom = Math.max(start.clientY, current.clientY);

  return {
    x: left,
    y: top,
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function getSelectableElement(element: Element | null): Element | null {
  return element?.closest(`[${SELECT_MODE_SELECTABLE_ATTRIBUTE}]`) ?? null;
}

function suppressSelectModeEvent(event: Event) {
  event.preventDefault();
  event.stopPropagation();
}

function getSelectableDataFromElement(element: Element): SelectableData | null {
  const selectablePayload = readSelectablePayload(
    element.getAttribute(SELECT_MODE_SELECTABLE_PAYLOAD_ATTRIBUTE),
  );
  if (!selectablePayload) {
    return null;
  }

  return exhaustiveSwitch(
    selectablePayload,
    "kind",
  )({
    plan: (selectablePlanPayload) => ({
      ...selectablePlanPayload,
      rect: getRectFromDomRect(element.getBoundingClientRect()),
    }),
  });
}

// Snapshot every selectable + its rect when a drag begins, minting a stable
// id per selectable so the parent overlay can key its live highlights.
function collectSelectableCandidates(): SelectableCandidate[] {
  return filterNulls(
    Array.from(
      document.querySelectorAll(`[${SELECT_MODE_SELECTABLE_ATTRIBUTE}]`),
    ).map((element) => {
      const data = getSelectableDataFromElement(element);
      return data
        ? { id: uuidv4(), data, payloadKey: getSelectablePayloadKey(element) }
        : null;
    }),
  );
}

/**
 * A drag captures a selectable when their boxes overlap (within
 * `SELECTION_TOLERANCE_PX`) — but NOT when the drag sits entirely inside
 * the selectable. The latter is the user dragging *within* a large card to
 * lasso things, not trying to select the card itself.
 */
function getCandidatesInSelection({
  candidates,
  selectionRect,
}: {
  candidates: SelectableCandidate[];
  selectionRect: SelectModeBoundingRect;
}): SelectableCandidate[] {
  return candidates.filter((candidate) => {
    return (
      isIntersecting(candidate.data.rect, selectionRect, {
        tolerance: SELECTION_TOLERANCE_PX,
      }) && !isRectEnclosing(candidate.data.rect, selectionRect)
    );
  });
}

function isPrimaryButtonPressed(event: PointerEvent): boolean {
  return (event.buttons & 1) === 1;
}

export function SelectableDetector() {
  const { agentUiState } = useBedrock();
  const selectionType = agentUiState?.selectionType ?? "none";
  const { displayMode, reportAreaSelection, reportDragRect, reportSelection } =
    useSelectModeInteractions();
  const dragSessionRef = React.useRef<DragSession | null>(IDLE_DRAG_SESSION);
  const suppressNextClickRef = React.useRef(false);
  // The parent owns "is anything selected?" — when it clears the selection
  // (composer dismissed / submitted) it pushes `selectionType: "none"` back
  // down, which is the iframe's only signal to drop the committed border.
  // eslint-disable-next-line replo/no-use-effect -- Mirror parent-driven selection clears into the in-iframe highlight.
  React.useEffect(() => {
    if (selectionType === "none") {
      setSelectedSelectableKeys([]);
    }
  }, [selectionType]);

  // eslint-disable-next-line replo/no-use-effect -- Select mode listens to document pointer events while active.
  React.useEffect(() => {
    if (displayMode !== "selection") {
      return;
    }

    const clearDrag = () => {
      const dragSession = dragSessionRef.current;
      if (dragSession?.hasExceededThreshold) {
        reportDragRect({ dragRect: null, selectionRects: [] });
      }
      suppressNextClickRef.current = false;
      dragSessionRef.current = IDLE_DRAG_SESSION;
    };

    const handlePointerDown = (event: PointerEvent) => {
      suppressNextClickRef.current = false;
      const targetElement =
        event.target instanceof Element ? event.target : null;
      const selectableElement = getSelectableElement(targetElement);
      if (selectableElement) {
        suppressSelectModeEvent(event);
      }
      dragSessionRef.current = {
        start: { clientX: event.clientX, clientY: event.clientY },
        current: { clientX: event.clientX, clientY: event.clientY },
        hasExceededThreshold: false,
        candidates: collectSelectableCandidates(),
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      // A press released outside this document (another frame, a context
      // menu) never reaches our pointerup, so a buttonless move ends the drag.
      if (dragSessionRef.current && !isPrimaryButtonPressed(event)) {
        clearDrag();
      }
      const dragSession = dragSessionRef.current;
      if (dragSession) {
        const nextDragSession = {
          ...dragSession,
          current: { clientX: event.clientX, clientY: event.clientY },
        };
        const rect = getSelectionRect({
          start: nextDragSession.start,
          current: nextDragSession.current,
        });
        const hasExceededThreshold =
          nextDragSession.hasExceededThreshold ||
          rect.width > DRAG_THRESHOLD_PX ||
          rect.height > DRAG_THRESHOLD_PX;
        dragSessionRef.current = {
          ...nextDragSession,
          hasExceededThreshold,
        };
        if (hasExceededThreshold) {
          suppressSelectModeEvent(event);
          suppressNextClickRef.current = true;
          reportDragRect({
            dragRect: rect,
            selectionRects: getCandidatesInSelection({
              candidates: nextDragSession.candidates,
              selectionRect: rect,
            }).map((candidate) => ({
              id: candidate.id,
              rect: candidate.data.rect,
            })),
          });
          setHoveredSelectableKey(null);
        }
        return;
      }

      const targetElement =
        event.target instanceof Element ? event.target : null;
      const selectableElement = getSelectableElement(targetElement);
      setHoveredSelectableKey(
        selectableElement ? getSelectablePayloadKey(selectableElement) : null,
      );
    };

    const handlePointerUp = (event: PointerEvent) => {
      const dragSession = dragSessionRef.current;
      clearDrag();

      if (dragSession?.hasExceededThreshold) {
        suppressSelectModeEvent(event);
        suppressNextClickRef.current = true;
        const selectionRect = getSelectionRect({
          start: dragSession.start,
          current: { clientX: event.clientX, clientY: event.clientY },
        });
        const selectedCandidates = getCandidatesInSelection({
          candidates: dragSession.candidates,
          selectionRect,
        });
        reportAreaSelection({
          rect: selectionRect,
          items: selectedCandidates.map((candidate) => candidate.data),
        });
        setHoveredSelectableKey(null);
        setSelectedSelectableKeys(
          selectedCandidates.map((candidate) => candidate.payloadKey),
        );
        return;
      }

      const targetElement =
        event.target instanceof Element ? event.target : null;
      const selectableElement = getSelectableElement(targetElement);
      if (!selectableElement) {
        return;
      }

      suppressSelectModeEvent(event);
      suppressNextClickRef.current = true;
      const selectedData = getSelectableDataFromElement(selectableElement);
      reportSelection(selectedData);
      setHoveredSelectableKey(null);
      setSelectedSelectableKeys(
        selectedData ? [getSelectablePayloadKey(selectableElement)] : [],
      );
    };

    const handleClick = (event: MouseEvent) => {
      if (suppressNextClickRef.current) {
        suppressSelectModeEvent(event);
        suppressNextClickRef.current = false;
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointermove", handlePointerMove, true);
    document.addEventListener("pointerup", handlePointerUp, true);
    document.addEventListener("pointercancel", clearDrag, true);
    document.addEventListener("click", handleClick, true);
    window.addEventListener("blur", clearDrag);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointermove", handlePointerMove, true);
      document.removeEventListener("pointerup", handlePointerUp, true);
      document.removeEventListener("pointercancel", clearDrag, true);
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("blur", clearDrag);
      clearDrag();
      // Leaving select mode (or unmounting) drops every in-iframe border.
      clearSelectionHighlights();
    };
  }, [displayMode, reportAreaSelection, reportDragRect, reportSelection]);

  return null;
}
